import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Copy } from "lucide-react";
import { dvStatus, dvRedeem, formatApiErrorDetail } from "../../lib/api";

export default function DimensionVSection({ refreshUser }) {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => dvStatus().then(setStatus).catch(() => setStatus(false));
  useEffect(() => { load(); }, []);

  const copy = async (code) => {
    try { await navigator.clipboard.writeText(code); toast.success("Credential copied."); }
    catch { toast.error("Could not copy."); }
  };

  const redeemNow = async (code) => {
    setBusy(true);
    try {
      await dvRedeem(code);
      toast.success("Dimension V has opened.");
      load();
      refreshUser && refreshUser();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Redemption failed.");
    } finally { setBusy(false); }
  };

  if (status === null) return <p className="font-mono text-xs text-white/30">CHECKING SIGNAL…</p>;
  if (status === false) return <p className="font-mono text-xs text-white/30">STATUS UNAVAILABLE.</p>;

  return (
    <div data-testid="account-dimension-v">
      {status.entitled ? (
        <div className="border border-gold/25 bg-[#0a0a08] p-10" data-testid="account-dv-active">
          <p className="font-mono text-[10px] tracking-[0.4em] text-gold">ACCESS ACTIVE</p>
          <p className="font-serif text-3xl text-white mt-4">The Fifth Dimension is open to you.</p>
          <p className="font-mono text-[9px] tracking-[0.25em] text-white/35 mt-3">
            {status.redeemedAt ? `VERIFIED ${new Date(status.redeemedAt).toLocaleDateString()}` : ""} · PERMANENT ENTITLEMENT
          </p>
          <Link to="/dimension-v" data-testid="account-dv-enter"
            className="inline-block mt-8 bg-gold text-black font-mono text-[11px] tracking-[0.25em] px-9 py-4 hover:bg-white transition-colors">
            ENTER DIMENSION V
          </Link>
        </div>
      ) : status.status === "review" ? (
        <div className="border border-white/10 p-10" data-testid="account-dv-review">
          <p className="font-mono text-[10px] tracking-[0.4em] text-red-400/80">ACCESS UNDER REVIEW</p>
          <p className="text-white/55 mt-4 leading-relaxed max-w-md">
            Your Dimension V access is temporarily under review. If you believe this is an error,
            contact us from the email associated with your qualifying order.
          </p>
        </div>
      ) : status.status === "revoked" ? (
        <div className="border border-white/10 p-10" data-testid="account-dv-revoked">
          <p className="font-mono text-[10px] tracking-[0.4em] text-white/40">ACCESS REVOKED</p>
          <p className="text-white/55 mt-4 leading-relaxed max-w-md">
            This account's Dimension V access has been revoked. Contact us if you believe this is an error.
          </p>
        </div>
      ) : status.credential ? (
        <div className="border border-gold/25 bg-[#0a0a08] p-10" data-testid="account-dv-credential">
          <p className="font-mono text-[10px] tracking-[0.4em] text-gold">YOUR ACCESS CREDENTIAL IS READY</p>
          <p className="text-white/55 mt-4 text-sm leading-relaxed max-w-md">
            Issued for your verified Multidimensional Complete Set order. Redeem it once — access then
            becomes a permanent part of this account.
          </p>
          <div className="flex flex-wrap items-center gap-4 mt-7">
            <code className="font-mono text-sm tracking-[0.15em] text-gold border border-gold/30 bg-black/40 px-5 py-3.5" data-testid="account-dv-code">
              {status.credential.code}
            </code>
            <button onClick={() => copy(status.credential.code)} data-testid="account-dv-copy" aria-label="Copy credential"
              className="w-11 h-11 border border-white/20 text-white/60 flex items-center justify-center hover:border-gold hover:text-gold transition-colors">
              <Copy size={15} />
            </button>
          </div>
          <button onClick={() => redeemNow(status.credential.code)} disabled={busy} data-testid="account-dv-redeem"
            className="mt-7 bg-gold text-black font-mono text-[11px] tracking-[0.25em] px-9 py-4 hover:bg-white transition-colors disabled:opacity-50">
            {busy ? "VERIFYING…" : "REDEEM NOW"}
          </button>
        </div>
      ) : (
        <div className="border-t border-white/10 py-10" data-testid="account-dv-none">
          <p className="font-serif text-2xl text-white/40">No Dimension V access on this account.</p>
          <p className="text-white/45 mt-4 text-sm leading-relaxed max-w-md">
            Access is reserved for verified owners of all four physical Dimensions — collected together through the Complete Set or separately.
            When your qualifying order is verified, your credential will appear here.
          </p>
          <div className="flex flex-wrap gap-6 mt-7">
            <Link to="/complete-set" data-testid="account-dv-learn"
              className="font-mono text-[11px] tracking-[0.25em] text-gold hover:text-white transition-colors">THE COMPLETE SET →</Link>
            <Link to="/dimension-v/access" data-testid="account-dv-redeem-link"
              className="font-mono text-[11px] tracking-[0.25em] text-white/45 hover:text-gold transition-colors">REDEEM A KEY →</Link>
          </div>
        </div>
      )}
    </div>
  );
}
